import { useEffect, useMemo, useState } from 'react';
import { addMonths, addWeeks, format } from 'date-fns';
import { Button, Flex } from '@mantine/core';
import { DatePicker } from '@mantine/dates';

import { ReactComponent as AlarmIcon } from "assets/blackAlarm.svg";

import styles from './FollowUpMenu.module.scss';

const DATE_BUTTON_FORMAT = 'EEE LLLL d, yyyy';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (date: string) => Promise<void>; // Date in ISO format (YYYY-MM-DD)
  onClear: () => Promise<void>;
}

const FollowUpMenu = ({ isOpen, onSubmit, onClose, onClear }: Props) => {
  const [isCustomDatePickerOpen, setIsCustomDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [nextWeek, nextMonth, nextQuarter, nextWeekStr, nextMonthStr, nextQuarterStr] = useMemo(() => {
    const today = new Date();

    const nextWeek = addWeeks(today, 1);
    const nextMonth = addMonths(today, 1);
    const nextQuarter = addMonths(today, 3);
    return [
      nextWeek,
      nextMonth,
      nextQuarter,
      format(nextWeek, 'yyyy-MM-dd'),
      format(nextMonth, 'yyyy-MM-dd'),
      format(nextQuarter, 'yyyy-MM-dd'),
    ];
  }, []);

  const handleCustomDateChange = (val: string | null) => {
    setSelectedDate(val);
  };

  const handleSelectDate = (date: string) => async () => {
    try {
      setSubmitting(true);
      await onSubmit(date);
    } finally {
      setSubmitting(false);
    }
  };

  const openCustomDatePicker = () => {
    setSelectedDate(null);
    setIsCustomDatePickerOpen(true);
  };

  const handleCancelCustomDatePicker = () => {
    setIsCustomDatePickerOpen(false);
    setSelectedDate(null);
  };

  const handleSubmitDate = async () => {
    if (selectedDate) {
      try {
        setSubmitting(true);
        await onSubmit(selectedDate);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleSubmitClear = async () => {
    try {
      setSubmitting(true);
      await onClear();
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setIsCustomDatePickerOpen(false);
      setSelectedDate(null);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className={styles.overlay} />
      <div className={styles.menu}>
        {isCustomDatePickerOpen ? (
          <>
            <div className={styles.followUpMenu}>
              <div className={styles.followUpMenuHeader}>
                <AlarmIcon width={25} height={25} />
                <span>Set Follow-Up Date</span>
              </div>
              <div className={styles.datepickerWrapper}>
                <DatePicker
                  value={selectedDate}
                  onChange={handleCustomDateChange}
                  hideOutsideDates
                  firstDayOfWeek={0}
                />
              </div>
              <Button
                type="button"
                color="#EEEEEE"
                fullWidth
                onClick={handleCancelCustomDatePicker}
                classNames={{ root: styles.cancelButton }}
              >
                CANCEL
              </Button>
            </div>
            <Button
              type="button"
              color="#EEEEEE"
              onClick={handleSubmitDate}
              classNames={{ root: styles.doneButton }}
              disabled={!selectedDate || submitting}
              loading={submitting}
            >
              Done
            </Button>
          </>
        ) : (
          <>
            <div className={styles.followUpMenu}>
              <div className={styles.followUpMenuHeader}>
                <AlarmIcon width={25} height={25} />
                <span>Set Follow-Up Date</span>
              </div>
              <Button
                type="button"
                fullWidth
                color={selectedDate === nextWeekStr ? "#E0E0E0" : "#F5F5F5"}
                p={8}
                onClick={handleSelectDate(nextWeekStr)}
                classNames={{ root: styles.dateOptionButton }}
              >
                <Flex direction="column" gap={5}>
                  <div>Next week</div>
                  <div className={styles.dateLabel}>{format(nextWeek, DATE_BUTTON_FORMAT)}</div>
                </Flex>
              </Button>
              <Button
                type="button"
                fullWidth
                p={8}
                color={selectedDate === nextMonthStr ? "#E0E0E0" : "#F5F5F5"}
                onClick={handleSelectDate(nextMonthStr)}
                classNames={{ root: styles.dateOptionButton }}
              >
                <Flex direction="column" gap={5}>
                  <div>In a Month</div>
                  <div className={styles.dateLabel}>{format(nextMonth, DATE_BUTTON_FORMAT)}</div>
                </Flex>
              </Button>
              <Button
                type="button"
                fullWidth
                p={8}
                color={selectedDate === nextQuarterStr ? "#E0E0E0" : "#F5F5F5"}
                onClick={handleSelectDate(nextQuarterStr)}
                classNames={{ root: styles.dateOptionButton }}
              >
                <Flex direction="column" gap={5}>
                  <div>In a Quarter</div>
                  <div className={styles.dateLabel}>{format(nextQuarter, DATE_BUTTON_FORMAT)}</div>
                </Flex>
              </Button>
              <Button
                type="button"
                fullWidth
                p={8}
                color="#F5F5F5"
                onClick={openCustomDatePicker}
                classNames={{ root: styles.dateOptionButton }}
              >
                <Flex direction="column" gap={5}>
                  <div>Custom</div>
                  <div className={styles.dateLabel}>Set Date</div>
                </Flex>
              </Button>
              <Button
                type="button"
                color="#EEEEEE"
                fullWidth
                onClick={onClose}
                classNames={{ root: styles.cancelButton }}
              >
                CANCEL
              </Button>
            </div>
            <Button
              type="button"
              color="#EEEEEE"
              onClick={handleSubmitClear}
              classNames={{ root: styles.cancelButton }}
              style={{ borderRadius: 10 }}
              disabled={submitting }
              loading={submitting}
            >
              Reset
            </Button>
          </>
        )}
      </div>
    </>
  );
};

export default FollowUpMenu;
